import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Button,
  Card,
  Form,
  Modal,
  Table,
  Spinner,
} from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import { motion, AnimatePresence } from 'framer-motion';
import NavContainer from './Nav';
import OffCanvasNav from './OffCanvasNav';
import AlertModal from './AlertModal';
import useAlertModal from '../hooks/useAlertModal';
import Pagination from './Pagination';
import { useAuth } from '../context/AuthContext';
import { dotoniwoofApi } from '../api/dotoniwooApi';
import { getCategoriesByType } from '../services/categoryApi';
import { getEmployees } from '../services/employeeApi';
import {
  getExpenses,
  getExpenseById,
  createExpense,
  updateExpense,
  deleteExpense,
  getExpenseReport,
  getExpenseReportDay,
  getExpenseReportCategory,
} from '../services/expenseApi';
import './ModernExpense.css';

const CATEGORY_LABEL = 'Gastos';
const EMPLOYEE_CATEGORIES = ['Sueldos', 'Comisiones'];

/** Devuelve las dos quincenas del mes de una fecha YYYY-MM-DD y cuál aplica según el día */
const getQuincenasForMonth = (dateStr) => {
  if (!dateStr) return [];
  const [y, m] = dateStr.split('-').map(Number);
  const lastDay = new Date(y, m, 0).getDate();
  return [
    { id: 1, label: 'Quincena 1', range: `1 - 15`, dayMin: 1, dayMax: 15 },
    { id: 2, label: 'Quincena 2', range: `16 - ${lastDay}`, dayMin: 16, dayMax: lastDay },
  ];
};

const getQuincenaForDate = (dateStr) => {
  if (!dateStr) return null;
  const day = parseInt(dateStr.split('-')[2], 10);
  return day <= 15 ? 1 : 2;
};

const getLocalDateString = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const initialForm = () => ({
  date: getLocalDateString(),
  type: '',
  description: '',
  amount: '',
  id_employee: '',
  sueldo: '',
  comision: '',
});

const ModernExpense = () => {
  const { user } = useAuth();
  const { showAlert, showAlertModal, alertMessage, alertType, hideAlert, getErrorMessage } = useAlertModal();

  const [expenseTypes, setExpenseTypes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState(initialForm());
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingExpense, setDeletingExpense] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [reportByMonth, setReportByMonth] = useState(null);
  const [reportByDay, setReportByDay] = useState(null);
  const [reportByCategory, setReportByCategory] = useState(null);
  const [reportYear, setReportYear] = useState(new Date().getFullYear());
  const [reportMonth, setReportMonth] = useState(String(new Date().getMonth() + 1).padStart(2, '0'));

  const [filterCategory, setFilterCategory] = useState('');
  const [filterMonth, setFilterMonth] = useState('');
  const [filterDay, setFilterDay] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const EXPENSES_PER_PAGE = 10;

  const showEmployee = useCallback(() => {
    if (!formData.type) return false;
    const list = Array.isArray(expenseTypes) ? expenseTypes : [];
    const cat = list.find((t) => String(t.id_category) === String(formData.type));
    return cat && EMPLOYEE_CATEGORIES.includes(cat.category_name);
  }, [formData.type, expenseTypes]);

  const isSueldosCategory = useCallback(() => {
    if (!formData.type) return false;
    const list = Array.isArray(expenseTypes) ? expenseTypes : [];
    const cat = list.find((t) => String(t.id_category) === String(formData.type));
    return cat && cat.category_name === 'Sueldos';
  }, [formData.type, expenseTypes]);

  const quincenas = getQuincenasForMonth(formData.date);
  const quincenaActual = getQuincenaForDate(formData.date);

  const getEmployeeDisplayName = useCallback((idEmployee) => {
    if (!idEmployee) return '';
    const list = Array.isArray(employees) ? employees : [];
    const emp = list.find((e) => String(e.id_employee) === String(idEmployee));
    if (!emp) return '';
    return emp.full_name || [emp.name, emp.surname, emp.lastname].filter(Boolean).join(' ').trim() || '';
  }, [employees]);

  const defaultDescriptionSueldos = useCallback(() => {
    const q = getQuincenaForDate(formData.date);
    if (q == null) return '';
    const base = `Pago de sueldo de la quincena ${q}`;
    const name = getEmployeeDisplayName(formData.id_employee);
    return name ? `${base} - ${name}` : base;
  }, [formData.date, formData.id_employee, getEmployeeDisplayName]);

  useEffect(() => {
    if (!formData.type || !expenseTypes.length) return;
    const list = Array.isArray(expenseTypes) ? expenseTypes : [];
    const cat = list.find((t) => String(t.id_category) === String(formData.type));
    if (!cat || cat.category_name !== 'Sueldos') return;
    const defaultDesc = defaultDescriptionSueldos();
    if (!defaultDesc) return;
    const isCurrentDefault = /^Pago de sueldo de la quincena [12]( - .*)?$/.test(formData.description.trim());
    if (formData.description.trim() === '' || isCurrentDefault) {
      setFormData((prev) => ({ ...prev, description: defaultDesc }));
    }
  }, [formData.type, formData.date, formData.id_employee, expenseTypes, defaultDescriptionSueldos]);

  useEffect(() => {
    if (!formData.type || !expenseTypes.length) return;
    const list = Array.isArray(expenseTypes) ? expenseTypes : [];
    const cat = list.find((t) => String(t.id_category) === String(formData.type));
    if (!cat || cat.category_name !== 'Sueldos') return;
    const su = parseFloat(formData.sueldo || 0) || 0;
    const co = parseFloat(formData.comision || 0) || 0;
    const total = su + co;
    const base = defaultDescriptionSueldos();
    if (!base) return;
    const fmt = (n) => (Number(n) || 0).toLocaleString('es-CO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const breakdown = ` Sueldo: $ ${fmt(su)}, Comisión: $ ${fmt(co)}, Total: $ ${fmt(total)}`;
    const fullDesc = base + breakdown;
    setFormData((prev) => (prev.description !== fullDesc ? { ...prev, description: fullDesc } : prev));
  }, [formData.type, formData.date, formData.id_employee, formData.sueldo, formData.comision, expenseTypes, defaultDescriptionSueldos]);

  const loadExpenseTypes = useCallback(async () => {
    try {
      const typeRes = await dotoniwoofApi.get(`/categoryType.php/${CATEGORY_LABEL}`);
      const typeData = typeRes.data;
      if (typeData.code !== 200 || !typeData.message?.[0]?.id_category_type) {
        setExpenseTypes([]);
        return;
      }
      const idCategoryType = typeData.message[0].id_category_type;
      const result = await getCategoriesByType(idCategoryType);
      if (!result.ok || result.data?.code !== 200) {
        setExpenseTypes([]);
        return;
      }
      const raw = result.data.message;
      setExpenseTypes(Array.isArray(raw) ? raw : []);
    } catch (err) {
      console.error('Error loading expense types:', err);
      setExpenseTypes([]);
    }
  }, []);

  const loadEmployees = useCallback(async () => {
    setEmployeesLoading(true);
    try {
      const result = await getEmployees();
      const list = result.ok ? (result.data?.message ?? result.data?.data ?? []) : [];
      setEmployees(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Error loading employees:', err);
      setEmployees([]);
    } finally {
      setEmployeesLoading(false);
    }
  }, []);

  const loadExpenses = useCallback(async () => {
    setLoading(true);
    const result = await getExpenses();
    setLoading(false);
    const raw = result.ok && result.data?.code === 200 ? result.data.message : null;
    setExpenses(Array.isArray(raw) ? raw : []);
  }, []);

  useEffect(() => {
    loadExpenseTypes();
  }, [loadExpenseTypes]);

  useEffect(() => {
    loadExpenses();
  }, [loadExpenses]);

  useEffect(() => {
    if (showEmployee()) {
      loadEmployees();
    } else {
      setEmployees([]);
    }
  }, [showEmployee, loadEmployees]);

  const loadReports = useCallback(async () => {
    const [rMonth, rDay, rCat] = await Promise.all([
      getExpenseReport(reportYear),
      getExpenseReportDay(reportMonth, reportYear),
      getExpenseReportCategory(reportMonth),
    ]);
    if (rMonth.ok && rMonth.data?.message?.[0]) setReportByMonth(rMonth.data.message[0]);
    else setReportByMonth(null);
    if (rDay.ok && rDay.data?.message?.[0]) setReportByDay(rDay.data.message[0]);
    else setReportByDay(null);
    if (rCat.ok && rCat.data?.message?.[0]) setReportByCategory(rCat.data.message[0]);
    else setReportByCategory(null);
  }, [reportYear, reportMonth]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const openCreate = () => {
    setFormData(initialForm());
    setEditingId(null);
    setShowModal(true);
  };

  const openEdit = async (id) => {
    const result = await getExpenseById(id);
    const ok = result.ok && (result.status === 200 || result.data?.code === 200);
    if (!ok) {
      showAlert(getErrorMessage(result?.data) || 'No se pudo cargar el gasto', 'error');
      return;
    }
    const raw = result.data;
    let e = null;
    if (raw?.message != null) {
      e = Array.isArray(raw.message) ? raw.message[0] : raw.message;
    } else if (raw?.data != null) {
      e = raw.data;
    } else if (raw && (raw.expense_date != null || raw.id_expense != null)) {
      e = raw;
    }
    if (!e || typeof e !== 'object') {
      showAlert('No se pudo cargar el gasto', 'error');
      return;
    }
    const dateStr = (e.expense_date || e.expense_date_created || '').toString().slice(0, 10);
    const idCategory = String(e.id_category ?? e.id_category_expense ?? '');
    const isSueldos = (Array.isArray(expenseTypes) ? expenseTypes : []).some(
      (t) => String(t.id_category) === idCategory && (t.category_name === 'Sueldos' || t.category === 'Sueldos')
    );
    setFormData({
      date: dateStr || getLocalDateString(),
      type: idCategory,
      description: (e.description ?? '').toString(),
      amount: String(e.amount ?? ''),
      id_employee: String(e.id_employee ?? ''),
      sueldo: isSueldos ? String(e.amount ?? '') : '',
      comision: '',
    });
    setEditingId(id);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setFormData(initialForm());
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const totalSueldos = isSueldosCategory()
    ? (parseFloat(formData.sueldo || 0) || 0) + (parseFloat(formData.comision || 0) || 0)
    : 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSueldosCategory() && totalSueldos <= 0) {
      showAlert('Ingrese el valor del sueldo y/o comisión', 'error');
      return;
    }
    setSubmitting(true);
    let description = formData.description;
    let amount = formData.amount;
    if (isSueldosCategory()) {
      const su = parseFloat(formData.sueldo || 0) || 0;
      const co = parseFloat(formData.comision || 0) || 0;
      const total = su + co;
      amount = String(total);
    }
    const payload = {
      firebase_UID: user.uid,
      id_category: formData.type,
      description,
      amount,
      expense_date: formData.date,
    };
    if (showEmployee() && formData.id_employee) {
      payload.id_employee = formData.id_employee;
    }
    try {
      if (editingId) {
        const result = await updateExpense(editingId, payload);
        const updateOk =
          result.ok &&
          (result.status === 200 ||
            result.status === 204 ||
            result.data?.code === 200 ||
            result.data?.code === 201 ||
            (result.data?.status === 'ok' && (result.data?.code === 200 || result.data?.code === 201)));
        if (updateOk) {
          showAlert(result.data?.message || 'Gasto actualizado', 'success');
          closeModal();
          loadExpenses();
          loadReports();
        } else {
          showAlert(getErrorMessage(result) || 'Error al actualizar', 'error');
        }
      } else {
        const result = await createExpense(payload);
        if (result.ok && result.data?.code === 201) {
          showAlert(result.data?.message || 'Gasto registrado correctamente', 'success');
          closeModal();
          loadExpenses();
          loadReports();
        } else {
          showAlert(getErrorMessage(result?.data) || 'Error al registrar el gasto', 'error');
        }
      }
    } catch (err) {
      showAlert(err?.message || 'Error de conexión', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const openDelete = (expense) => {
    setDeletingExpense(expense);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deletingExpense?.id_expense) return;
    setDeleting(true);
    const result = await deleteExpense(deletingExpense.id_expense, user?.uid);
    setDeleting(false);
    setShowDeleteModal(false);
    setDeletingExpense(null);
    if (result.ok && (result.data?.code === 200 || result.data?.code === 204)) {
      showAlert(result.data?.message || 'Gasto eliminado', 'success');
      loadExpenses();
      loadReports();
    } else {
      showAlert(getErrorMessage(result?.data) || 'Error al eliminar', 'error');
    }
  };

  const formatMoney = (n) => `$ ${(Number(n) || 0).toLocaleString('es-CO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const filteredExpenses = useMemo(() => {
    return expenses.filter((ex) => {
      if (filterCategory) {
        const cat = expenseTypes.find((t) => String(t.id_category) === String(filterCategory));
        const match = cat && (String(ex.id_category) === String(filterCategory) || ex.category === cat.category_name);
        if (!match) return false;
      }
      if (filterMonth && ex.expense_date && ex.expense_date.slice(0, 7) !== `${reportYear}-${filterMonth}`) return false;
      if (filterDay && ex.expense_date && ex.expense_date.slice(8, 10) !== filterDay) return false;
      return true;
    });
  }, [expenses, filterCategory, filterMonth, filterDay, reportYear, expenseTypes]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterCategory, filterMonth, filterDay]);

  const totalPages = Math.max(1, Math.ceil(filteredExpenses.length / EXPENSES_PER_PAGE));
  const paginatedExpenses = useMemo(() => {
    const start = (currentPage - 1) * EXPENSES_PER_PAGE;
    return filteredExpenses.slice(start, start + EXPENSES_PER_PAGE);
  }, [filteredExpenses, currentPage]);

  const totalAmount = useMemo(
    () => filteredExpenses.reduce((sum, ex) => sum + (parseFloat(ex.amount) || 0), 0),
    [filteredExpenses]
  );

  const selectedCategoryName = filterCategory
    ? (Array.isArray(expenseTypes) ? expenseTypes : []).find((t) => String(t.id_category) === String(filterCategory))?.category_name || 'Seleccionada'
    : null;

  const vsPreviousMonth = useMemo(() => {
    const currentKey = `${reportYear}-${reportMonth}`;
    const prevMonth = reportMonth === '01' ? 12 : parseInt(reportMonth, 10) - 1;
    const prevYear = reportMonth === '01' ? reportYear - 1 : reportYear;
    const previousKey = `${prevYear}-${String(prevMonth).padStart(2, '0')}`;
    const currentTotal = expenses
      .filter((ex) => ex.expense_date && ex.expense_date.slice(0, 7) === currentKey)
      .reduce((s, ex) => s + (parseFloat(ex.amount) || 0), 0);
    const previousTotal = expenses
      .filter((ex) => ex.expense_date && ex.expense_date.slice(0, 7) === previousKey)
      .reduce((s, ex) => s + (parseFloat(ex.amount) || 0), 0);
    let pct = 0;
    if (previousTotal === 0) pct = currentTotal > 0 ? 100 : 0;
    else pct = ((currentTotal - previousTotal) / previousTotal) * 100;
    const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    const currentLabel = `${monthNames[parseInt(reportMonth, 10) - 1]} ${reportYear}`;
    const previousLabel = `${monthNames[prevMonth - 1]} ${prevYear}`;
    return { currentTotal, previousTotal, pct, currentLabel, previousLabel };
  }, [expenses, reportYear, reportMonth]);

  return (
    <>
      <Helmet>
        <title>Gastos - DotoniWoof</title>
      </Helmet>
      <NavContainer />
      <OffCanvasNav />

      <div className="modern-expense-container">
        <div className="page-header">
          <h1 className="page-title">Registro de gastos</h1>
          <Button className="btn-primary-expense" onClick={openCreate}>
            + Registrar gasto
          </Button>
        </div>

        <AnimatePresence mode="wait">
          {loading ? (
            <motion.div
              key="loading"
              className="expense-loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <Spinner animation="border" />
              <span className="ms-2">Cargando gastos...</span>
            </motion.div>
          ) : (
            <motion.div
              key="content"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
            >
              <Card className="expense-card">
                <Card.Header className="expense-card-header">
                  <h5>Resumen del período</h5>
                </Card.Header>
                <Card.Body>
                  <div className="reports-section">
                    <div className="report-card">
                      <h6>Total listado</h6>
                      <div className="report-value">{formatMoney(totalAmount)}</div>
                      <small className="text-muted">{filteredExpenses.length} gasto(s){filterCategory || filterMonth || filterDay ? ' (filtrado)' : ''}</small>
                    </div>
                    {reportByMonth?.expenses && (
                      <div className="report-card">
                        <h6>Reporte por mes (año {reportYear})</h6>
                        <div className="report-value">
                          {formatMoney(reportByMonth.expenses.reduce((a, b) => a + (Number(b) || 0), 0))}
                        </div>
                      </div>
                    )}
                    {selectedCategoryName ? (
                      <div className="report-card">
                        <h6>Por categoría: {selectedCategoryName}</h6>
                        <div className="report-value">{formatMoney(totalAmount)}</div>
                        <small className="text-muted">{filteredExpenses.length} gasto(s)</small>
                      </div>
                    ) : reportByCategory?.expenses ? (
                      <div className="report-card">
                        <h6>Por categoría (mes {reportMonth})</h6>
                        <div className="report-value">
                          {formatMoney(reportByCategory.expenses.reduce((a, b) => a + (Number(b) || 0), 0))}
                        </div>
                      </div>
                    ) : null}
                  </div>
                  <div className="report-filters">
                    <Form.Select
                      value={reportYear}
                      onChange={(e) => setReportYear(Number(e.target.value))}
                      className="form-select"
                    >
                      {[2023, 2024, 2025, 2026].map((y) => (
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </Form.Select>
                    <Form.Select
                      value={reportMonth}
                      onChange={(e) => setReportMonth(e.target.value)}
                      className="form-select"
                    >
                      {Array.from({ length: 12 }, (_, i) => {
                        const m = String(i + 1).padStart(2, '0');
                        return <option key={m} value={m}>Mes {m}</option>;
                      })}
                    </Form.Select>
                  </div>
                  <div className="expense-vs-previous-section">
                    <h6 className="expense-vs-previous-title">Comparativo vs mes pasado</h6>
                    <div className="expense-vs-previous-content">
                      <div className={`expense-vs-previous-pct ${vsPreviousMonth.pct >= 0 ? 'expense-vs-up' : 'expense-vs-down'}`}>
                        {vsPreviousMonth.pct >= 0 ? '+' : ''}{vsPreviousMonth.pct.toFixed(1)}%
                      </div>
                      <p className="expense-vs-previous-desc mb-0">
                        {vsPreviousMonth.pct >= 0 ? 'Más gastos' : 'Menos gastos'} que el mes anterior
                      </p>
                      <div className="expense-vs-previous-detail">
                        <span>{vsPreviousMonth.currentLabel}: {formatMoney(vsPreviousMonth.currentTotal)}</span>
                        <span>{vsPreviousMonth.previousLabel}: {formatMoney(vsPreviousMonth.previousTotal)}</span>
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>

              <Card className="expense-card">
                <Card.Header className="expense-card-header">
                  <h5>Listado de gastos</h5>
                </Card.Header>
                <Card.Body>
                  <div className="expense-filters-modern">
                    <div className="expense-filters-bar">
                      <div className="expense-filter-group">
                        <label className="expense-filter-label">Categoría</label>
                        <Form.Select
                          className="expense-filter-select"
                          value={filterCategory}
                          onChange={(e) => setFilterCategory(e.target.value)}
                          title="Filtrar por categoría"
                        >
                          <option value="">Todas</option>
                          {(Array.isArray(expenseTypes) ? expenseTypes : []).map((t) => (
                            <option key={t.id_category} value={t.id_category}>{t.category_name}</option>
                          ))}
                        </Form.Select>
                      </div>
                      <div className="expense-filter-group">
                        <label className="expense-filter-label">Mes</label>
                        <Form.Select
                          className="expense-filter-select"
                          value={filterMonth}
                          onChange={(e) => setFilterMonth(e.target.value)}
                          title="Filtrar por mes"
                        >
                          <option value="">Todos</option>
                          {Array.from({ length: 12 }, (_, i) => {
                            const m = String(i + 1).padStart(2, '0');
                            const monthName = new Date(2000, i, 1).toLocaleString('es', { month: 'long' });
                            return <option key={m} value={m}>{monthName}</option>;
                          })}
                        </Form.Select>
                      </div>
                      <div className="expense-filter-group">
                        <label className="expense-filter-label">Día</label>
                        <Form.Select
                          className="expense-filter-select"
                          value={filterDay}
                          onChange={(e) => setFilterDay(e.target.value)}
                          title="Filtrar por día del mes"
                        >
                          <option value="">Todos</option>
                          {Array.from({ length: 31 }, (_, i) => {
                            const d = String(i + 1).padStart(2, '0');
                            return <option key={d} value={d}>{d}</option>;
                          })}
                        </Form.Select>
                      </div>
                      {(filterCategory || filterMonth || filterDay) && (
                        <button
                          type="button"
                          className="expense-filter-clear-btn"
                          onClick={() => { setFilterCategory(''); setFilterMonth(''); setFilterDay(''); setCurrentPage(1); }}
                        >
                          <span className="expense-filter-clear-icon" aria-hidden>×</span>
                          Limpiar
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="expense-table-wrap">
                    {filteredExpenses.length === 0 ? (
                      <div className="expense-empty">
                        <div className="empty-icon">📋</div>
                        <p className="mb-0">
                          {expenses.length === 0
                            ? 'No hay gastos registrados. Registra el primero desde el botón superior.'
                            : 'No hay gastos que coincidan con los filtros.'}
                        </p>
                      </div>
                    ) : (
                      <>
                        <Table className="expense-table" responsive>
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>Fecha</th>
                              <th>Categoría</th>
                              <th>Descripción</th>
                              <th>Valor</th>
                              <th>Empleado</th>
                              <th className="text-end">Acciones</th>
                            </tr>
                          </thead>
                          <tbody>
                            {paginatedExpenses.map((expense) => (
                            <tr key={expense.id_expense}>
                              <td>{expense.id_expense}</td>
                              <td>{expense.expense_date}</td>
                              <td>{expense.category}</td>
                              <td>{expense.description}</td>
                              <td className="amount-cell">{formatMoney(expense.amount)}</td>
                              <td>{expense.employee_name ?? '-'}</td>
                              <td className="actions-cell text-end">
                                <Button
                                  variant="link"
                                  className="btn-icon btn-icon-edit"
                                  onClick={() => openEdit(expense.id_expense)}
                                  title="Editar"
                                >
                                  ✏️
                                </Button>
                                <Button
                                  variant="link"
                                  className="btn-icon btn-icon-delete"
                                  onClick={() => openDelete(expense)}
                                  title="Eliminar"
                                >
                                  🗑️
                                </Button>
                              </td>
                            </tr>
                          ))}
                          </tbody>
                        </Table>
                        {totalPages > 1 && (
                          <div className="expense-pagination-wrap d-flex justify-content-between align-items-center flex-wrap gap-2 mt-3 pt-3 border-top">
                            <small className="text-muted">
                              Página {currentPage} de {totalPages} ({filteredExpenses.length} gastos)
                            </small>
                            <Pagination
                              currentPage={currentPage}
                              totalPages={totalPages}
                              paginate={setCurrentPage}
                            />
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <Modal show={showModal} onHide={closeModal} centered className="modern-expense-modal">
        <Modal.Header closeButton>
          <Modal.Title>{editingId ? 'Editar gasto' : 'Registrar gasto'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Fecha</Form.Label>
              <Form.Control
                type="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Tipo de gasto</Form.Label>
              <Form.Select name="type" value={formData.type} onChange={handleChange} required>
                <option value="">Seleccione un tipo</option>
                {(Array.isArray(expenseTypes) ? expenseTypes : []).map((t) => (
                  <option key={t.id_category} value={t.id_category}>{t.category_name}</option>
                ))}
              </Form.Select>
            </Form.Group>
            {isSueldosCategory() && (
              <Form.Group className="mb-3">
                <Form.Label className="d-block">Quincena a pagar</Form.Label>
                <div className="quincena-pills">
                  {quincenas.map((q) => {
                    const isCurrent = q.id === quincenaActual;
                    return (
                      <span
                        key={q.id}
                        className={`quincena-pill ${isCurrent ? 'quincena-pill-active' : ''}`}
                        title={`${q.label}: ${q.range}`}
                      >
                        <span className="quincena-pill-label">{q.label}</span>
                        <span className="quincena-pill-range">{q.range}</span>
                      </span>
                    );
                  })}
                </div>
                <Form.Text className="text-muted small">
                  {quincenaActual != null && quincenas[quincenaActual - 1] && (
                    <>Según la fecha seleccionada corresponde la <strong>{quincenas[quincenaActual - 1].label}</strong> ({quincenas[quincenaActual - 1].range}).</>
                  )}
                </Form.Text>
              </Form.Group>
            )}
            {showEmployee() && (
              <Form.Group className="mb-3">
                <Form.Label>Empleado</Form.Label>
                <input type="hidden" name="id_employee" value={formData.id_employee} required={showEmployee()} />
                {employeesLoading ? (
                  <div className="employee-avatar-loading">
                    <Spinner animation="border" size="sm" />
                    <span className="ms-2">Cargando empleados...</span>
                  </div>
                ) : (
                  <div className="employee-avatar-grid">
                    {(Array.isArray(employees) ? employees : []).map((emp) => {
                      const displayName = emp.full_name || [emp.name, emp.surname, emp.lastname].filter(Boolean).join(' ').trim() || `Empleado ${emp.id_employee}`;
                      const isSelected = String(formData.id_employee) === String(emp.id_employee);
                      return (
                        <button
                          key={emp.id_employee}
                          type="button"
                          className={`employee-avatar-item ${isSelected ? 'selected' : ''}`}
                          onClick={() => setFormData((prev) => ({ ...prev, id_employee: String(emp.id_employee) }))}
                          title={displayName}
                        >
                          <div className="employee-avatar-circle">
                            {emp.photo_url || emp.avatar_url || emp.image_url ? (
                              <img src={emp.photo_url || emp.avatar_url || emp.image_url} alt={displayName} />
                            ) : (
                              <span className="employee-avatar-icon" aria-hidden>👤</span>
                            )}
                          </div>
                          <span className="employee-avatar-name">{displayName}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
                {showEmployee() && !employeesLoading && (!Array.isArray(employees) || employees.length === 0) && (
                  <p className="text-muted small mb-0 mt-2">No hay empleados registrados.</p>
                )}
              </Form.Group>
            )}
            {isSueldosCategory() && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Sueldo</Form.Label>
                  <Form.Control
                    type="number"
                    name="sueldo"
                    value={formData.sueldo}
                    onChange={handleChange}
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Comisión</Form.Label>
                  <Form.Control
                    type="number"
                    name="comision"
                    value={formData.comision}
                    onChange={handleChange}
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                  />
                </Form.Group>
                <div className="expense-total-sueldos mb-3">
                  <span className="expense-total-label">Total a pagar:</span>
                  <span className="expense-total-value">{formatMoney(totalSueldos)}</span>
                </div>
              </>
            )}
            <Form.Group className="mb-3">
              <Form.Label>Descripción</Form.Label>
              <Form.Control
                type="text"
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
              />
            </Form.Group>
            {!isSueldosCategory() && (
              <Form.Group className="mb-3">
                <Form.Label>Valor</Form.Label>
                <Form.Control
                  type="number"
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                  required
                />
              </Form.Group>
            )}
            <div className="d-flex gap-2 justify-content-end">
              <Button variant="outline-secondary" onClick={closeModal} disabled={submitting}>
                Cancelar
              </Button>
              <Button type="submit" className="btn-submit-expense" disabled={submitting}>
                {submitting ? <Spinner animation="border" size="sm" /> : (editingId ? 'Guardar cambios' : 'Guardar gasto')}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered className="modern-expense-modal modal-delete">
        <Modal.Header closeButton>
          <Modal.Title>Eliminar gasto</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {deletingExpense && (
            <p className="mb-0">
              ¿Eliminar el gasto &quot;{deletingExpense.description}&quot; del {deletingExpense.expense_date} por {formatMoney(deletingExpense.amount)}? Esta acción no se puede deshacer.
            </p>
          )}
          <div className="d-flex gap-2 justify-content-end mt-3">
            <Button variant="outline-secondary" onClick={() => setShowDeleteModal(false)} disabled={deleting}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={confirmDelete} disabled={deleting}>
              {deleting ? <Spinner animation="border" size="sm" /> : 'Eliminar'}
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      <AlertModal show={showAlertModal} onHide={hideAlert} message={alertMessage} type={alertType} />
    </>
  );
};

export default ModernExpense;
